/**
 * @fileoverview TemplateCategoryCard Component
 *
 * Individual category card in the budget template grid. Displays editable
 * target amount and percentage fields for each category in the budget template.
 * Shows blank inputs when values are 0 for easier editing.
 *
 * @module components/BudgetTemplate/TemplateCategoryCard
 */
import { useState, useEffect } from 'react';
import MoneyInput from '../common/MoneyInput';
import styles from './TemplateCategoryCard.module.css';

/**
 * TemplateCategoryCard Component
 *
 * Individual category card in the budget template grid
 */
function TemplateCategoryCard({
    category,
    settings,
    handleUpdateTemplate,
    handleRemoveCategory,
    isCategoryCeased,
    readOnly
}) {
    // Local state for priority group input to prevent jumping while typing
    const [localPriorityGroup, setLocalPriorityGroup] = useState(
        settings.priorityGroup === 1 || !settings.priorityGroup ? '' : settings.priorityGroup
    );

    // Sync local state when parent settings change
    useEffect(() => {
        setLocalPriorityGroup(
            settings.priorityGroup === 1 || !settings.priorityGroup ? '' : settings.priorityGroup
        );
    }, [settings.priorityGroup]);

    // Handle priority group input change (local state only)
    const handlePriorityGroupChange = (e) => {
        setLocalPriorityGroup(e.target.value);
    };

    // Handle priority group blur (update parent state)
    const handlePriorityGroupBlur = () => {
        handleUpdateTemplate(category, 'priorityGroup', localPriorityGroup);
    };

    return (
        <div className={styles.card}>
            <div className={styles.header}>
                <strong className={styles.categoryName}>
                    {category}
                    {isCategoryCeased(category) && (
                        <span className={styles.ceasedBadge}>
                            Ceased
                        </span>
                    )}
                </strong>
                {!readOnly && (
                    <button
                        onClick={() => handleRemoveCategory(category)}
                        className={styles.removeButton}
                        aria-label="Remove category"
                    >
                        <span className={styles.removeButtonText}>Remove</span>
                        <span className={styles.removeButtonIcon}>×</span>
                    </button>
                )}
            </div>

            <div className={styles.inputsContainer}>
                {/* Target Amount Input */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Target Amount:
                    </label>
                    <MoneyInput
                        value={settings.target === 0 ? null : settings.target}
                        onChange={(parsedValue) => handleUpdateTemplate(category, 'target', parsedValue !== null ? parsedValue.toString() : '0')}
                        disabled={readOnly}
                        min={0}
                        max={9999999.99}
                        placeholder="0.00"
                        className={styles.input}
                    />
                </div>

                {/* Percentage Input */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Percentage:
                    </label>
                    <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={settings.percentage === 0 ? '' : settings.percentage}
                        onChange={(e) => handleUpdateTemplate(category, 'percentage', e.target.value)}
                        disabled={readOnly}
                        className={styles.input}
                    />
                </div>

                {/* Priority Group Input */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Priority Group:
                    </label>
                    <input
                        type="number"
                        step="1"
                        min="1"
                        max="99"
                        value={localPriorityGroup}
                        onChange={handlePriorityGroupChange}
                        onBlur={handlePriorityGroupBlur}
                        disabled={readOnly}
                        className={styles.input}
                        placeholder="1"
                    />
                </div>
            </div>
        </div>
    );
}

export default TemplateCategoryCard;
